import React from 'react';
import { Pill } from '@/components/ui/Pill';
import { LazySection } from '@/components/ui/LazySection';
import Link from 'next/link';
import { Building2, ExternalLink } from 'lucide-react';

interface PlatformDirectorySectionProps {
  filteredPlatforms: any[];
  viewMode: 'card' | 'list';
  extractFields: (platform: any) => any;
  technologyOptions: string[];
  productOptions: { id: string; name: string }[];
  technologyLookup: Record<string, string>;
  selectedProviders: string[];
  selectedTechnologies: string[];
  selectedProducts: string[];
  selectedSamples: string[];
  selectedReadouts: string[];
  selectedPlexTypes: string[];
  selectedChemistry: string[];
  selectedResolution: string[];
  selectedThroughput: string[];
  selectedCountries: string[];
}

export default function PlatformDirectorySection({
  filteredPlatforms,
  viewMode,
  extractFields,
  technologyOptions,
  productOptions,
  technologyLookup,
  selectedProviders,
  selectedTechnologies,
  selectedProducts,
  selectedSamples,
  selectedReadouts,
  selectedPlexTypes,
  selectedChemistry,
  selectedResolution,
  selectedThroughput,
  selectedCountries,
}: PlatformDirectorySectionProps) {
  if (viewMode === 'list') {
    return (
      <LazySection minHeight={400}>
        <div className="space-y-4">
          {filteredPlatforms.map((platform: any, idx: number) => {
            const { logoSrc, providerLogoSrc, instrumentImageSrc, name, description, website, websiteUrl, location, slug } = extractFields(platform);
            
            return (
              <div key={platform.id || idx} className="bg-white rounded-lg shadow-lg flex flex-col hover:shadow-xl transition-shadow duration-200">
                                 <div className="flex items-stretch">
                   {/* Split Image Container - Same as Grid View */}
                   <div className={`flex-shrink-0 w-96 flex bg-neutral-100 rounded-l-lg overflow-hidden shadow-md ${platform.pills && platform.pills.length > 0 ? 'h-48' : 'h-40'}`}>
                    {/* Provider Logo - Left 40% */}
                    <div className="w-2/5 flex justify-center items-center bg-neutral-100">
                      {providerLogoSrc ? (
                        <img src={providerLogoSrc} alt={`${name} provider logo`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-neutral-400" />
                        </div>
                      )}
                    </div>
                    {/* Instrument Image - Right 60% */}
                    <div className="w-3/5 flex justify-center items-center bg-neutral-100">
                      {instrumentImageSrc ? (
                        <img src={instrumentImageSrc} alt={`${name} instrument`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 17L12 22L22 17" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 12L12 17L22 12" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0 p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-display font-bold text-xl text-primary-700 truncate">
                        {slug ? (
                          <Link href={`/platforms/${slug}`} className="hover:text-primary-600 transition-colors">
                            {name}
                          </Link>
                        ) : (
                          name
                        )}
                      </h3>
                      {websiteUrl && (
                        <a
                          href={websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 ml-2 text-neutral-500 hover:text-neutral-700 transition-colors"
                          aria-label={`Visit ${name} website`}
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                    
                    {location && (
                      <p className="text-sm text-neutral-600 mb-2 italic">{location}</p>
                    )}
                    
                    {description && (
                      <p className="text-sm text-neutral-700 mb-3 line-clamp-2">{description}</p>
                    )}
                    
                    {/* Pills for platform-specific fields */}
                    <div className="flex flex-wrap gap-2">
                      {selectedTechnologies.length > 0 && platform['Technology'] && Array.isArray(platform['Technology']) && [...new Set(platform['Technology'].map((tech: any) => {
                        const techId = typeof tech === 'string' ? tech : (tech.id || tech);
                        return technologyLookup[techId] || techId;
                      }).filter((techName: string) => selectedTechnologies.includes(techName)))].map((techName: string, i: number) => (
                        <Pill key={i} type="platformTechnology">{techName}</Pill>
                      ))}
                      {selectedSamples.length > 0 && platform['Sample Types'] && Array.isArray(platform['Sample Types']) && [...new Set(platform['Sample Types'].filter((sample: string) => selectedSamples.includes(sample)))].map((sample: string, i: number) => (
                        <Pill key={i} type="platformSample">{sample}</Pill>
                      ))}
                      {selectedReadouts.length > 0 && platform['Readout'] && Array.isArray(platform['Readout']) && [...new Set(platform['Readout'].filter((readout: string) => selectedReadouts.includes(readout)))].map((readout: string, i: number) => (
                        <Pill key={i} type="platformReadout">{readout}</Pill>
                      ))}
                      {selectedPlexTypes.length > 0 && platform['Plex Type'] && Array.isArray(platform['Plex Type']) && [...new Set(platform['Plex Type'].filter((plex: string) => selectedPlexTypes.includes(plex)))].map((plex: string, i: number) => (
                        <Pill key={i} type="platformPlexType">{plex}</Pill>
                      ))}
                      {selectedChemistry.length > 0 && platform['Chemistry'] && Array.isArray(platform['Chemistry']) && [...new Set(platform['Chemistry'].filter((chem: string) => selectedChemistry.includes(chem)))].map((chem: string, i: number) => (
                        <Pill key={i} type="platformChemistry">{chem}</Pill>
                      ))}
                      {selectedResolution.length > 0 && platform['Cellular Resolution'] && Array.isArray(platform['Cellular Resolution']) && [...new Set(platform['Cellular Resolution'].filter((res: string) => selectedResolution.includes(res)))].map((res: string, i: number) => (
                        <Pill key={i} type="platformResolution">{res}</Pill>
                      ))}
                      {selectedThroughput.length > 0 && platform['Throughput'] && Array.isArray(platform['Throughput']) && [...new Set(platform['Throughput'].filter((thru: string) => selectedThroughput.includes(thru)))].map((thru: string, i: number) => (
                        <Pill key={i} type="platformThroughput">{thru}</Pill>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </LazySection>
    );
  }

  // Card view
  return (
    <LazySection minHeight={400}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlatforms.map((platform: any, idx: number) => {
          const { logoSrc, providerLogoSrc, instrumentImageSrc, name, description, website, websiteUrl, location, slug } = extractFields(platform);
          
          // If we have a slug, make the entire card clickable
          if (slug) {
            return (
              <Link
                key={platform.id || idx}
                href={`/platforms/${slug}`}
                className="block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
                aria-label={`View details for ${name}`}
              >
                <div className="bg-white rounded-lg shadow-lg h-full flex flex-col hover:shadow-xl transition-shadow duration-200 cursor-pointer">
                  {/* Logo Section - Split Container with Provider Logo and Instrument Image */}
                  <div className="w-full flex bg-neutral-100 rounded-t-lg overflow-hidden shadow-md" style={{ height: 160 }}>
                    {/* Provider Logo - Left 40% */}
                    <div className="w-2/5 flex justify-center items-center bg-neutral-100">
                      {providerLogoSrc ? (
                        <img 
                          src={providerLogoSrc} 
                          alt={`${name} provider logo`} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-neutral-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Instrument Image - Right 60% */}
                    <div className="w-3/5 flex justify-center items-center bg-neutral-100">
                      {instrumentImageSrc ? (
                        <img 
                          src={instrumentImageSrc} 
                          alt={`${name} instrument`} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 17L12 22L22 17" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 12L12 17L22 12" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Content Section - Flexible but with consistent spacing */}
                  <div className="flex-1 flex flex-col p-6">
                    <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                      {name}
                    </h3>
                    
                    {location && (
                      <p className="text-sm text-neutral-600 mb-2 italic">
                        {location}
                      </p>
                    )}
                    
                    {description && (
                      <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                        {description}
                      </p>
                    )}
                    
                    {/* Pills Section - Fixed at bottom */}
                    <div className="mt-auto">
                      <div className="flex flex-wrap gap-2">
                        {selectedTechnologies.length > 0 && platform['Technology'] && Array.isArray(platform['Technology']) && [...new Set(platform['Technology'].map((tech: any) => {
                          const techId = typeof tech === 'string' ? tech : (tech.id || tech);
                          return technologyLookup[techId] || techId;
                        }).filter((techName: string) => selectedTechnologies.includes(techName)))].slice(0, 2).map((techName: string, i: number) => (
                          <Pill key={i} type="platformTechnology">{techName}</Pill>
                        ))}
                        {selectedSamples.length > 0 && platform['Sample Types'] && Array.isArray(platform['Sample Types']) && [...new Set(platform['Sample Types'].filter((sample: string) => selectedSamples.includes(sample)))].slice(0, 2).map((sample: string, i: number) => (
                          <Pill key={i} type="platformSample">{sample}</Pill>
                        ))}
                        {selectedReadouts.length > 0 && platform['Readout'] && Array.isArray(platform['Readout']) && [...new Set(platform['Readout'].filter((readout: string) => selectedReadouts.includes(readout)))].slice(0, 2).map((readout: string, i: number) => (
                          <Pill key={i} type="platformReadout">{readout}</Pill>
                        ))}
                        {selectedPlexTypes.length > 0 && platform['Plex Type'] && Array.isArray(platform['Plex Type']) && [...new Set(platform['Plex Type'].filter((plex: string) => selectedPlexTypes.includes(plex)))].slice(0, 2).map((plex: string, i: number) => (
                          <Pill key={i} type="platformPlexType">{plex}</Pill>
                        ))}
                        {selectedChemistry.length > 0 && platform['Chemistry'] && Array.isArray(platform['Chemistry']) && [...new Set(platform['Chemistry'].filter((chem: string) => selectedChemistry.includes(chem)))].slice(0, 2).map((chem: string, i: number) => (
                          <Pill key={i} type="platformChemistry">{chem}</Pill>
                        ))}
                                            {selectedResolution.length > 0 && platform['Cellular Resolution'] && Array.isArray(platform['Cellular Resolution']) && [...new Set(platform['Cellular Resolution'].filter((res: string) => selectedResolution.includes(res)))].slice(0, 2).map((res: string, i: number) => (
                      <Pill key={i} type="platformResolution">{res}</Pill>
                    ))}
                        {selectedThroughput.length > 0 && platform['Throughput'] && Array.isArray(platform['Throughput']) && [...new Set(platform['Throughput'].filter((thru: string) => selectedThroughput.includes(thru)))].slice(0, 2).map((thru: string, i: number) => (
                          <Pill key={i} type="platformThroughput">{thru}</Pill>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          }

          // If no slug, render as a regular div with website link
          return (
            <div key={platform.id || idx} className="bg-white rounded-lg shadow-lg h-full flex flex-col">
              {/* Logo Section - Split Container with Provider Logo and Instrument Image */}
              <div className="w-full flex bg-neutral-100 rounded-t-lg overflow-hidden shadow-md" style={{ height: 160 }}>
                {/* Provider Logo - Left Third */}
                <div className="w-1/3 flex justify-center items-center bg-neutral-100">
                  {providerLogoSrc ? (
                    <img 
                      src={providerLogoSrc} 
                      alt={`${name} provider logo`} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-neutral-400" />
                    </div>
                  )}
                </div>
                
                {/* Instrument Image - Right Two Thirds */}
                <div className="w-2/3 flex justify-center items-center bg-neutral-100">
                  {instrumentImageSrc ? (
                    <img 
                      src={instrumentImageSrc} 
                      alt={`${name} instrument`} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 17L12 22L22 17" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 12L12 17L22 12" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Content Section - Flexible but with consistent spacing */}
              <div className="flex-1 flex flex-col p-6">
                <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                  {name}
                </h3>
                
                {location && (
                  <p className="text-sm text-neutral-600 mb-2 italic">
                    {location}
                  </p>
                )}
                
                {description && (
                  <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                    {description}
                  </p>
                )}
                
                {/* Pills Section - Fixed at bottom */}
                <div className="mt-auto">
                  <div className="flex flex-wrap gap-2">
                    {selectedTechnologies.length > 0 && platform['Technology'] && Array.isArray(platform['Technology']) && [...new Set(platform['Technology'].map((tech: any) => {
                      const techId = typeof tech === 'string' ? tech : (tech.id || tech);
                      return technologyLookup[techId] || techId;
                    }).filter((techName: string) => selectedTechnologies.includes(techName)))].slice(0, 2).map((techName: string, i: number) => (
                      <Pill key={i} type="platformTechnology">{techName}</Pill>
                    ))}
                    {selectedSamples.length > 0 && platform['Sample Types'] && Array.isArray(platform['Sample Types']) && [...new Set(platform['Sample Types'].filter((sample: string) => selectedSamples.includes(sample)))].slice(0, 2).map((sample: string, i: number) => (
                      <Pill key={i} type="platformSample">{sample}</Pill>
                    ))}
                    {selectedReadouts.length > 0 && platform['Readout'] && Array.isArray(platform['Readout']) && [...new Set(platform['Readout'].filter((readout: string) => selectedReadouts.includes(readout)))].slice(0, 2).map((readout: string, i: number) => (
                      <Pill key={i} type="platformReadout">{readout}</Pill>
                    ))}
                    {selectedPlexTypes.length > 0 && platform['Plex Type'] && Array.isArray(platform['Plex Type']) && [...new Set(platform['Plex Type'].filter((plex: string) => selectedPlexTypes.includes(plex)))].slice(0, 2).map((plex: string, i: number) => (
                      <Pill key={i} type="platformPlexType">{plex}</Pill>
                    ))}
                    {selectedChemistry.length > 0 && platform['Chemistry'] && Array.isArray(platform['Chemistry']) && [...new Set(platform['Chemistry'].filter((chem: string) => selectedChemistry.includes(chem)))].slice(0, 2).map((chem: string, i: number) => (
                      <Pill key={i} type="platformChemistry">{chem}</Pill>
                    ))}
                    {selectedResolution.length > 0 && platform['Cellular Resolution'] && Array.isArray(platform['Cellular Resolution']) && [...new Set(platform['Cellular Resolution'].filter((res: string) => selectedResolution.includes(res)))].slice(0, 2).map((res: string, i: number) => (
                      <Pill key={i} type="platformResolution">{res}</Pill>
                    ))}
                    {selectedThroughput.length > 0 && platform['Throughput'] && Array.isArray(platform['Throughput']) && [...new Set(platform['Throughput'].filter((thru: string) => selectedThroughput.includes(thru)))].slice(0, 2).map((thru: string, i: number) => (
                      <Pill key={i} type="platformThroughput">{thru}</Pill>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </LazySection>
  );
} 